import { Button, Divider, LoadingDots, MaxWidth } from '@advanza/ui'
import Icon from '@advanza/ui/Icon'
import ReclaimActionsMenu from 'components/reclaims/ReclaimActionsMenu'
import ReclaimTaskOverview from 'components/reclaims/ReclaimTaskOverview'
import Page from 'components/ui/Page'
import { format } from 'date-fns'
import { useAppSelector } from 'hooks/hooks'
import { useReclaimsList } from 'hooks/reclaimHooks'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

const useQuery = () => new URLSearchParams(useLocation().search)

const ReclaimTaskPage = () => {
    const query = useQuery()
    const navigate = useNavigate()
    const filterId = query.get('filterId') || 'all'
    const lastXMonths = query.get('lastXMonths') ? parseInt(query.get('lastXMonths') as string) : 1
    const serviceIds = query.get('serviceIds')
        ? query
              .get('serviceIds')
              ?.split(',')
              .map((id) => parseInt(id))
        : undefined
    const status = query.get('status') || undefined
    const leadType = query.get('leadType') || undefined
    const reasonIds = query.get('reasonIds')
        ? query
              .get('reasonIds')
              ?.split(',')
              .map((id) => parseInt(id))
        : undefined
    const searchQuery = query.get('query') || undefined
    const hidePendingAi = query.get('hidePendingAi') ? query.get('hidePendingAi') === 'true' : true

    const { countries } = useAppSelector((state) => state.countries)
    const combinedOptions = useMemo(
        () => ({
            lastXMonths,
            countries,
            serviceIds,
            status,
            leadType,
            reasonIds,
            query: searchQuery,
            hidePendingAi,
            noLimit: true,
        }),
        [
            lastXMonths,
            countries,
            serviceIds,
            status,
            leadType,
            reasonIds,
            searchQuery,
            hidePendingAi,
        ],
    )

    const { currentPage, entities, isFetching, error, refresh, count } = useReclaimsList(
        filterId,
        combinedOptions,
    )

    // Get the reclaim ID from URL or default to 0
    const urlReclaimId = query.get('reclaimId')

    // Don't set initial state until we have data
    const [currentIndex, setCurrentIndex] = useState<number | null>(null)

    // Handle initialization and filter changes in a single effect
    useEffect(() => {
        if (currentPage.length > 0 && !isFetching) {
            if (currentIndex === null) {
                // Initial load - set the starting position
                if (urlReclaimId) {
                    const index = currentPage.findIndex(
                        (id: number) => id.toString() === urlReclaimId,
                    )
                    setCurrentIndex(index >= 0 ? index : 0)
                } else {
                    setCurrentIndex(0)
                }
            } else if (urlReclaimId) {
                // Check if the URL reclaim ID is still valid after filter changes
                const index = currentPage.findIndex((id: number) => id.toString() === urlReclaimId)
                if (index === -1) {
                    // URL reclaim ID is no longer valid, reset to 0
                    setCurrentIndex(0)
                }
            }
        }
    }, [currentPage, isFetching, urlReclaimId, currentIndex])

    // Update URL when currentIndex changes, but only after data is loaded
    useEffect(() => {
        if (
            currentPage.length > 0 &&
            currentIndex !== null &&
            currentIndex < currentPage.length &&
            !isFetching
        ) {
            const currentReclaimId = currentPage[currentIndex]
            const newUrl = new URLSearchParams(query.toString())
            newUrl.set('reclaimId', currentReclaimId.toString())

            // Only update URL if it's different to avoid unnecessary navigation
            if (urlReclaimId !== currentReclaimId.toString()) {
                navigate(`?${newUrl.toString()}`, { replace: true })
            }
        }
    }, [currentIndex, currentPage, navigate, query, urlReclaimId, isFetching])

    useEffect(() => {
        if (countries && countries.length > 0) {
            refresh(true)
        }
    }, [countries])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') goPrev()
            if (e.key === 'ArrowRight') goNext()
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [currentIndex, currentPage])

    const hasPrev = currentIndex !== null && currentIndex > 0
    const hasNext = currentIndex !== null && currentIndex < currentPage.length - 1

    const goPrev = () => {
        if (hasPrev) setCurrentIndex(currentIndex! - 1)
    }

    const goNext = () => {
        if (hasNext) setCurrentIndex(currentIndex! + 1)
    }

    const currentReclaimId = currentPage[currentIndex!]
    const currentReclaim = entities?.reclaims?.[currentReclaimId]

    return (
        <Page title="Reclaim Task">
            <MaxWidth>
                <Divider m />
                <div
                    className="sticky top-0 z-20 pt-[10px] pb-[10px] border-b border-grey-200"
                    style={{ backgroundColor: '#fafafa' }}>
                    <div className="grid grid-cols-3 items-center mb-4">
                        <div className="flex justify-start">
                            <Button
                                onClick={() => navigate('/billing/reclaims')}
                                variant="outline"
                                size="sm">
                                <Icon name="arrow_back" className="mr-2" />
                                Back to Reclaims List
                            </Button>
                        </div>
                        <div className="flex justify-center">
                            <h1 className="text-display-xs font-semibold">
                                {isFetching && !currentReclaim ? (
                                    <LoadingDots size="sm" />
                                ) : (
                                    `${currentPage.length > 0 ? currentIndex! + 1 : 0} of ${
                                        currentPage.length
                                    }${
                                        count && count > currentPage.length
                                            ? ` (${count} total)`
                                            : ''
                                    }`
                                )}
                            </h1>
                        </div>
                        <div className="flex justify-end items-center gap-2">
                            <Button
                                onClick={goPrev}
                                disabled={!hasPrev || (isFetching && !currentReclaim)}
                                className={
                                    !hasPrev || (isFetching && !currentReclaim)
                                        ? 'opacity-50 cursor-not-allowed'
                                        : ''
                                }>
                                <Icon name="chevron_left" fontSize={28} classNameIcon="!mr-0" />
                            </Button>
                            <Button
                                onClick={goNext}
                                disabled={!hasNext || (isFetching && !currentReclaim)}
                                className={
                                    !hasNext || (isFetching && !currentReclaim)
                                        ? 'opacity-50 cursor-not-allowed'
                                        : ''
                                }>
                                <Icon name={'chevron_right'} fontSize={28} classNameIcon="!mr-0" />
                            </Button>
                        </div>
                    </div>
                    <Divider />
                    <div className="grid grid-cols-3 items-center w-full gap-4">
                        <div className="min-w-0">
                            <h2 className="text-xl font-bold mb-4">
                                Reclaim ID: {currentReclaimId}
                            </h2>
                        </div>

                        <div className="min-w-0 relative -top-1 flex items-center justify-center">
                            {currentReclaim && (
                                <ReclaimActionsMenu
                                    key={currentReclaimId}
                                    reclaim={currentReclaim}
                                    onDone={goNext}
                                />
                            )}
                        </div>

                        <div className="min-w-0 text-right">
                            {currentReclaim && (
                                <div className="text-md text-neutral-500">
                                    <div>
                                        <b>Created:</b>{' '}
                                        {format(
                                            new Date(currentReclaim.created ?? ''),
                                            'd-MMM-yyyy',
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <Divider m />
                {isFetching && !currentReclaim ? (
                    <div className="flex justify-center items-center p-10">
                        <LoadingDots size="lg" />
                    </div>
                ) : error ? (
                    <div className="text-red-500 p-4 border border-red-300 rounded">
                        Error loading reclaims: {error.message || 'Unknown error'}
                    </div>
                ) : currentPage.length > 0 && currentIndex !== null && currentReclaim ? (
                    <ReclaimTaskOverview reclaim={currentReclaim} notes={entities?.notes} />
                ) : (
                    <p className="text-neutral-500 italic">
                        {currentIndex === null
                            ? 'Loading reclaims...'
                            : 'No reclaims found for the current filter.'}
                    </p>
                )}
            </MaxWidth>
        </Page>
    )
}

export default ReclaimTaskPage
